import { Checkbox, List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRefinementList } from 'react-instantsearch';
import { Tag_Rule_Entity_Type_Enum } from '@kheops/graphql';
import FilterDisplay, { FilterDisplayMode } from '../../../search/filters/FilterDisplay';
import { useTagRulesQuery } from '../../../queries/__generated__/tagRules.generated';
import { getSocialTagFromTagRule, SocialTag } from '../../../common/utils/social-tags.utils';

export interface SocialTagFilterProps {
  entity: Tag_Rule_Entity_Type_Enum;
  level: number;
  label: string;
  mode: FilterDisplayMode;
}

export default function SocialTagFilter({ entity, level, label, mode }: SocialTagFilterProps): React.JSX.Element {
  const { t } = useTranslation(['social-proof']);
  const attribute = `social_tags_lvl${level}`;
  const { items, refine } = useRefinementList({ limit: 1000, attribute });
  const { data } = useTagRulesQuery({ variables: { entity } });
  const [selectedSocialTags, setSelectedSocialTags] = useState<string[]>([]);

  const socialTagItems = useMemo((): SocialTag[] => {
    return (data?.tag_rule || [])
      .map((tagRule) => getSocialTagFromTagRule(tagRule))
      .filter((tagRule) => tagRule.level === level)
      .sort((a, b) => a.priority - b.priority);
  }, [data]);

  const content = useMemo((): React.JSX.Element => {
    const listItems = socialTagItems.reduce((acc, { type, priority }) => {
      const itemIndex = socialTagItems.findIndex((item) => item.type === type);
      // Social tags are recorded as type|level|priority format
      const rawValue = `${type}|${level}|${priority}`;

      if (itemIndex > -1) {
        acc.push((
          <ListItemButton
            key={type}
            onClick={() => refine(rawValue)}
          >
            <ListItemText primary={t(`social-proof:${type.toLowerCase()}.title`)} />
            <ListItemIcon>
              <Checkbox
                edge="start"
                checked={!!selectedSocialTags.includes(rawValue)}
              />
            </ListItemIcon>
          </ListItemButton>
        ));
      }

      return acc;
    }, [] as React.JSX.Element[]);

    return (
      <List>
        {listItems}
      </List>
    );
  }, [socialTagItems, selectedSocialTags]);

  useEffect((): void => {
    if (items.length) {
      const refinedItems = items
        .filter(({ isRefined }) => isRefined)
        .map(({ value }) => value);

      setSelectedSocialTags(refinedItems);
    }
  }, [items]);

  return (
    <FilterDisplay
      mode={mode}
      label={label}
      activeItemCount={selectedSocialTags.length}
      content={content}
      name={attribute}
    />
  );
}
